import React, { useCallback, useRef, useState } from "react";
import { SupportingInfo } from "modules/SupportingInfo/supportingInfo";
import { SelectWithSearchAndFilters } from "shared/components/Inputs/SelectWithSearchAndFilters";
import { Wysiwyg } from "shared/components/Inputs/Wysiwyg";
import { TextField } from "@mui/material";

const UpdateTimeoutTime = 1000;

export const RelatedSupportingInfoInput = (props: SupportingInfoInputProps) => {
  const {
    index,
    info,
    onSelectSupportingInfo,
    onContentUpdate,
    infoOptions,
    isLoadingInfoOptions,
    setIsSaving,
    onTabNameUpdate,
    hideContentEditing,
  } = props;
  const updateTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const [relatedInfoValues, setRelatedInfoValues] = useState<{ tabName?: string; content?: string }>({});

  const handleUpdateValues = useCallback(
    (values: { tabName?: string; content?: string }) => {
      setIsSaving(true);
      const valuesToSet = { ...relatedInfoValues, ...values };
      setRelatedInfoValues(valuesToSet);
      clearTimeout(updateTimeout.current);
      updateTimeout.current = setTimeout(() => {
        if (valuesToSet.content) {
          onContentUpdate(index, valuesToSet.content);
        }
        if (valuesToSet.tabName !== undefined) {
          onTabNameUpdate(index, valuesToSet.tabName);
        }
        setRelatedInfoValues({});
      }, UpdateTimeoutTime);
    },
    [index, onContentUpdate, onTabNameUpdate, relatedInfoValues, setIsSaving]
  );

  return (
    <div className="_d-grid">
      <SelectWithSearchAndFilters
        selectedOption={
          info.id
            ? {
                value: String(info.supportingInformation.parent || info.supportingInformation.id),
                label: info.supportingInformation.title || "",
              }
            : undefined
        }
        isOptionsLoading={isLoadingInfoOptions}
        options={infoOptions.map((info) => ({
          value: String(info.id),
          label: info.title,
          onSelect: (value) => onSelectSupportingInfo(index, value),
        }))}
        placeholder="Select supporting information"
      />
      {info.id && !hideContentEditing && (
        <TextField
          value={relatedInfoValues.tabName !== undefined ? relatedInfoValues.tabName : info.tabName ?? ''}
          onChange={(e) => handleUpdateValues({ tabName: e.target.value })}
          label="Tab Name"
        />
      )}
      {info.id && !hideContentEditing && (
        <Wysiwyg
          savedData={info.supportingInformation.content}
          onChange={(content: string) => handleUpdateValues({ content })}
        />
      )}
    </div>
  );
};

interface SupportingInfoInputProps {
  index: number;
  info: SupportingInfo.PartialRelatedSupportingInfo;
  infoOptions: SupportingInfo.SupportingInfo[];
  onSelectSupportingInfo: (index: number, infoId: number | string) => void;
  onContentUpdate: (index: number, content: string) => void;
  onTabNameUpdate: (index: number, tabName: string) => void;
  isLoadingInfoOptions: boolean;
  setIsSaving: (isSaving: boolean) => void;
  hideContentEditing?: boolean;
}
